import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Pagination,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  CardGiftcard as GiftIcon,
  BarChart as ChartIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import spinService from '../services/spinService';
import { useToast } from '../contexts/ToastContext';

const SpinManagement = () => {
  const { showToast } = useToast();
  const [currentTab, setCurrentTab] = useState(0);
  
  // Prizes states
  const [prizes, setPrizes] = useState([]);
  const [loadingPrizes, setLoadingPrizes] = useState(false);
  const [prizeDialogOpen, setPrizeDialogOpen] = useState(false);
  const [editingPrize, setEditingPrize] = useState(null);
  const [prizeForm, setPrizeForm] = useState({
    name: '',
    description: '',
    type: 'cash',
    value: 0,
    cashAmount: 0,
    discountPercentage: 0,
    discountCode: '',
    itemName: '',
    itemDescription: '',
    pointsAmount: 0,
    image: '',
    weight: 1,
    stock: -1,
    displayOrder: 0,
    isActive: true,
  });

  // Config states
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({
    isEnabled: true,
    activeDays: [0, 1, 2, 3, 4, 5, 6],
    startTime: '00:00',
    endTime: '23:59',
    popupBehavior: 'once_per_day',
    frequency: 'daily',
    showOnHomeLoad: true,
    popupTitle: 'Spin & Win!',
    popupDescription: 'Spin the wheel and win amazing prizes!',
  });

  // Preselected winners states
  const [preselectedWinners, setPreselectedWinners] = useState([]);
  const [loadingWinners, setLoadingWinners] = useState(false);
  const [winnerDialogOpen, setWinnerDialogOpen] = useState(false);
  const [bulkWinnerDialogOpen, setBulkWinnerDialogOpen] = useState(false);
  const [winnerForm, setWinnerForm] = useState({
    userId: '',
    prizeId: '',
    week: '',
  });
  const [bulkWinnerForm, setBulkWinnerForm] = useState({
    winners: [{ userId: '', prizeId: '' }],
    week: '',
  });

  // Statistics states
  const [spins, setSpins] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingSpins, setLoadingSpins] = useState(false);
  const [spinsPage, setSpinsPage] = useState(1);
  const [spinsTotalPages, setSpinsTotalPages] = useState(1);
  const [processingSpin, setProcessingSpin] = useState(null);
  const [spinStatusFilter, setSpinStatusFilter] = useState('all');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (currentTab === 0) {
      fetchPrizes();
    } else if (currentTab === 1) {
      fetchConfig();
    } else if (currentTab === 2) {
      fetchPreselectedWinners();
    } else if (currentTab === 3) {
      fetchSpins();
      fetchStatistics();
    }
  }, [currentTab, spinsPage, spinStatusFilter]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Prize Management
  const fetchPrizes = async () => {
    try {
      setLoadingPrizes(true);
      const response = await spinService.getAllPrizes();
      if (response.status) {
        setPrizes(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching prizes:', error);
      showSnackbar('Failed to fetch prizes', 'error');
    } finally {
      setLoadingPrizes(false);
    }
  };

  const handleOpenPrizeDialog = (prize = null) => {
    if (prize) {
      setEditingPrize(prize);
      setPrizeForm({
        name: prize.name || '',
        description: prize.description || '',
        type: prize.type || 'cash',
        value: prize.value || 0,
        cashAmount: prize.cashAmount || 0,
        discountPercentage: prize.discountPercentage || 0,
        discountCode: prize.discountCode || '',
        itemName: prize.itemName || '',
        itemDescription: prize.itemDescription || '',
        pointsAmount: prize.pointsAmount || 0,
        image: prize.image || '',
        weight: prize.weight || 1,
        stock: prize.stock !== undefined ? prize.stock : -1,
        displayOrder: prize.displayOrder || 0,
        isActive: prize.isActive !== undefined ? prize.isActive : true,
      });
    } else {
      setEditingPrize(null);
      setPrizeForm({
        name: '',
        description: '',
        type: 'cash',
        value: 0,
        cashAmount: 0,
        discountPercentage: 0,
        discountCode: '',
        itemName: '',
        itemDescription: '',
        pointsAmount: 0,
        image: '',
        weight: 1,
        stock: -1,
        displayOrder: 0,
        isActive: true,
      });
    }
    setPrizeDialogOpen(true);
  };

  const handleSavePrize = async () => {
    try {
      const prizeData = {
        ...prizeForm,
        value: parseFloat(prizeForm.value) || 0,
        weight: parseInt(prizeForm.weight) || 1,
        stock: prizeForm.stock === '' ? -1 : parseInt(prizeForm.stock) || -1,
        displayOrder: parseInt(prizeForm.displayOrder) || 0,
      };

      if (editingPrize) {
        await spinService.updatePrize(editingPrize._id, prizeData);
        showSnackbar('Prize updated successfully', 'success');
      } else {
        await spinService.createPrize(prizeData);
        showSnackbar('Prize created successfully', 'success');
      }
      setPrizeDialogOpen(false);
      fetchPrizes();
    } catch (error) {
      console.error('Error saving prize:', error);
      showSnackbar(error.message || 'Failed to save prize', 'error');
    }
  };

  const handleDeletePrize = async (id) => {
    if (window.confirm('Are you sure you want to delete this prize?')) {
      try {
        await spinService.deletePrize(id);
        showSnackbar('Prize deleted successfully', 'success');
        fetchPrizes();
      } catch (error) {
        console.error('Error deleting prize:', error);
        showSnackbar(error.message || 'Failed to delete prize', 'error');
      }
    }
  };

  // Configuration
  const fetchConfig = async () => {
    try {
      setLoadingConfig(true);
      const response = await spinService.getConfig();
      if (response.status && response.data) {
        setConfig(response.data);
        setConfigForm({
          isEnabled: response.data.isEnabled !== undefined ? response.data.isEnabled : true,
          activeDays: response.data.activeDays || [0, 1, 2, 3, 4, 5, 6],
          startTime: response.data.startTime || '00:00',
          endTime: response.data.endTime || '23:59',
          popupBehavior: response.data.popupBehavior || 'once_per_day',
          frequency: response.data.frequency || 'daily',
          showOnHomeLoad: response.data.showOnHomeLoad !== undefined ? response.data.showOnHomeLoad : true,
          popupTitle: response.data.popupTitle || 'Spin & Win!',
          popupDescription: response.data.popupDescription || 'Spin the wheel and win amazing prizes!',
        });
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      showSnackbar('Failed to load configuration', 'error');
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSavingConfig(true);
      await spinService.updateConfig(configForm);
      showSnackbar('Configuration updated successfully', 'success');
      fetchConfig();
    } catch (error) {
      console.error('Error saving config:', error);
      showSnackbar(error.message || 'Failed to save configuration', 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleDayToggle = (day) => {
    setConfigForm(prev => ({
      ...prev,
      activeDays: prev.activeDays.includes(day)
        ? prev.activeDays.filter(d => d !== day)
        : [...prev.activeDays, day],
    }));
  };

  // Preselected Winners
  const fetchPreselectedWinners = async () => {
    try {
      setLoadingWinners(true);
      const response = await spinService.getPreselectedWinners();
      if (response.status) {
        setPreselectedWinners(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching preselected winners:', error);
      showSnackbar('Failed to fetch preselected winners', 'error');
    } finally {
      setLoadingWinners(false);
    }
  };

  const handleSaveWinner = async () => {
    try {
      await spinService.createPreselectedWinner(winnerForm);
      showSnackbar('Preselected winner created successfully', 'success');
      setWinnerDialogOpen(false);
      setWinnerForm({ userId: '', prizeId: '', week: '' });
      fetchPreselectedWinners();
    } catch (error) {
      console.error('Error saving winner:', error);
      showSnackbar(error.message || 'Failed to save preselected winner', 'error');
    }
  };

  const handleSaveBulkWinners = async () => {
    try {
      await spinService.bulkCreatePreselectedWinners(bulkWinnerForm);
      showSnackbar('Preselected winners created successfully', 'success');
      setBulkWinnerDialogOpen(false);
      setBulkWinnerForm({ winners: [{ userId: '', prizeId: '' }], week: '' });
      fetchPreselectedWinners();
    } catch (error) {
      console.error('Error saving bulk winners:', error);
      showSnackbar(error.message || 'Failed to save preselected winners', 'error');
    }
  };

  const handleDeleteWinner = async (id) => {
    if (window.confirm('Are you sure you want to delete this preselected winner?')) {
      try {
        await spinService.deletePreselectedWinner(id);
        showSnackbar('Preselected winner deleted successfully', 'success');
        fetchPreselectedWinners();
      } catch (error) {
        console.error('Error deleting winner:', error);
        showSnackbar(error.message || 'Failed to delete preselected winner', 'error');
      }
    }
  };

  // Statistics
  const fetchSpins = async () => {
    try {
      setLoadingSpins(true);
      const params = {
        page: spinsPage.toString(),
        limit: '20',
      };
      if (spinStatusFilter !== 'all') {
        params.status = spinStatusFilter;
      }
      const response = await spinService.getAllSpins(params);
      if (response.status && response.data) {
        setSpins(response.data.spins || []);
        setSpinsTotalPages(response.data.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching spins:', error);
      showSnackbar('Failed to fetch spins', 'error');
    } finally {
      setLoadingSpins(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await spinService.getSpinStatistics();
      if (response.status && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleClaimSpin = async (id) => {
    if (window.confirm('Are you sure you want to claim this spin? This will process the prize for the user.')) {
      try {
        setProcessingSpin(id);
        const response = await spinService.claimSpin(id);
        if (response.status) {
          showSnackbar('Spin claimed successfully', 'success');
          fetchSpins();
          fetchStatistics();
        }
      } catch (error) {
        console.error('Error claiming spin:', error);
        showSnackbar(error.message || 'Failed to claim spin', 'error');
      } finally {
        setProcessingSpin(null);
      }
    }
  };

  const handleCancelSpin = async (id) => {
    if (window.confirm('Are you sure you want to cancel this spin? This will refund the prize stock if applicable.')) {
      try {
        setProcessingSpin(id);
        const response = await spinService.cancelSpin(id);
        if (response.status) {
          showSnackbar('Spin cancelled successfully', 'success');
          fetchSpins();
          fetchStatistics();
        }
      } catch (error) {
        console.error('Error cancelling spin:', error);
        showSnackbar(error.message || 'Failed to cancel spin', 'error');
      } finally {
        setProcessingSpin(null);
      }
    }
  };

  const getPrizeTypeColor = (type) => {
    switch (type) {
      case 'cash': return 'success';
      case 'discount': return 'info';
      case 'item': return 'warning';
      case 'points': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'claimed': return 'success';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Spin & Win Management
      </Typography>

      <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<GiftIcon />} label="Prizes" />
        <Tab icon={<SettingsIcon />} label="Configuration" />
        <Tab icon={<PeopleIcon />} label="Preselected Winners" />
        <Tab icon={<ChartIcon />} label="Statistics" />
      </Tabs>

      {/* Prizes Tab */}
      {currentTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Prizes Management</Typography>
              <Box>
                <IconButton onClick={fetchPrizes} color="primary">
                  <RefreshIcon />
                </IconButton>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenPrizeDialog()}
                  sx={{ ml: 1 }}
                >
                  Add Prize
                </Button>
              </Box>
            </Box>

            {loadingPrizes ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Weight</TableCell>
                      <TableCell>Stock</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prizes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No prizes found
                        </TableCell>
                      </TableRow>
                    ) : (
                      prizes.map((prize) => (
                        <TableRow key={prize._id}>
                          <TableCell>{prize.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={prize.type}
                              color={getPrizeTypeColor(prize.type)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {prize.type === 'cash' && `₦${prize.cashAmount || prize.value}`}
                            {prize.type === 'discount' && `${prize.discountPercentage}%`}
                            {prize.type === 'points' && `${prize.pointsAmount} pts`}
                            {prize.type === 'item' && prize.itemName}
                            {!['cash', 'discount', 'points', 'item'].includes(prize.type) && prize.value}
                          </TableCell>
                          <TableCell>{prize.weight || 1}</TableCell>
                          <TableCell>
                            {prize.stock === -1 ? 'Unlimited' : `${prize.stockUsed || 0}/${prize.stock}`}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={prize.isActive ? 'Active' : 'Inactive'}
                              color={prize.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPrizeDialog(prize)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeletePrize(prize._id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Configuration Tab */}
      {currentTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Spin Configuration</Typography>
              <Button
                variant="contained"
                onClick={handleSaveConfig}
                disabled={savingConfig}
                startIcon={savingConfig ? <CircularProgress size={16} /> : <SettingsIcon />}
              >
                Save Configuration
              </Button>
            </Box>

            {loadingConfig ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={configForm.isEnabled}
                        onChange={(e) => setConfigForm({ ...configForm, isEnabled: e.target.checked })}
                      />
                    }
                    label="Enable Spin Feature"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Active Days
                  </Typography>
                  <FormGroup row>
                    {dayNames.map((day, index) => (
                      <FormControlLabel
                        key={index}
                        control={
                          <Checkbox
                            checked={configForm.activeDays.includes(index)}
                            onChange={() => handleDayToggle(index)}
                          />
                        }
                        label={day.substring(0, 3)}
                      />
                    ))}
                  </FormGroup>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Start Time"
                    type="time"
                    value={configForm.startTime}
                    onChange={(e) => setConfigForm({ ...configForm, startTime: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="End Time"
                    type="time"
                    value={configForm.endTime}
                    onChange={(e) => setConfigForm({ ...configForm, endTime: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Popup Behavior</InputLabel>
                    <Select
                      value={configForm.popupBehavior}
                      onChange={(e) => setConfigForm({ ...configForm, popupBehavior: e.target.value })}
                      label="Popup Behavior"
                    >
                      <MenuItem value="always">Always Show</MenuItem>
                      <MenuItem value="once_per_day">Once Per Day</MenuItem>
                      <MenuItem value="once_per_week">Once Per Week</MenuItem>
                      <MenuItem value="once_per_config">Once Per Config</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {configForm.popupBehavior === 'once_per_config' && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Frequency</InputLabel>
                      <Select
                        value={configForm.frequency}
                        onChange={(e) => setConfigForm({ ...configForm, frequency: e.target.value })}
                        label="Frequency"
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={configForm.showOnHomeLoad}
                        onChange={(e) => setConfigForm({ ...configForm, showOnHomeLoad: e.target.checked })}
                      />
                    }
                    label="Show Popup on Home Load"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Popup Title"
                    value={configForm.popupTitle}
                    onChange={(e) => setConfigForm({ ...configForm, popupTitle: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Popup Description"
                    multiline
                    rows={3}
                    value={configForm.popupDescription}
                    onChange={(e) => setConfigForm({ ...configForm, popupDescription: e.target.value })}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preselected Winners Tab */}
      {currentTab === 2 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Preselected Winners</Typography>
              <Box>
                <IconButton onClick={fetchPreselectedWinners} color="primary">
                  <RefreshIcon />
                </IconButton>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setWinnerDialogOpen(true)}
                  sx={{ ml: 1 }}
                >
                  Add Winner
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setBulkWinnerDialogOpen(true)}
                  sx={{ ml: 1 }}
                >
                  Bulk Add
                </Button>
              </Box>
            </Box>

            {loadingWinners ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Prize</TableCell>
                      <TableCell>Week</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preselectedWinners.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No preselected winners found
                        </TableCell>
                      </TableRow>
                    ) : (
                      preselectedWinners.map((winner) => (
                        <TableRow key={winner._id}>
                          <TableCell>
                            {winner.userId?.fullname || winner.userId?.username || winner.userId}
                          </TableCell>
                          <TableCell>
                            {winner.prizeId ? (winner.prizeId.name || 'Random') : 'Random'}
                          </TableCell>
                          <TableCell>{winner.week}</TableCell>
                          <TableCell>
                            <Chip
                              label={winner.status}
                              color={getStatusColor(winner.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteWinner(winner._id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Statistics Tab */}
      {currentTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total Spins
                </Typography>
                <Typography variant="h4" color="primary">
                  {stats?.totalSpins || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total Winners
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats?.totalWinners || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Active Prizes
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats?.activePrizes || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pending Spins
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats?.pendingSpins || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Recent Spins
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Filter by Status</InputLabel>
                    <Select
                      value={spinStatusFilter}
                      onChange={(e) => {
                        setSpinStatusFilter(e.target.value);
                        setSpinsPage(1);
                      }}
                      label="Filter by Status"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="claimed">Claimed</MenuItem>
                      <MenuItem value="expired">Expired</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {loadingSpins ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Prize</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {spins.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                No spins found
                              </TableCell>
                            </TableRow>
                          ) : (
                            spins.map((spin) => (
                              <TableRow key={spin._id}>
                                <TableCell>
                                  {spin.userId?.fullname || spin.userId?.username || spin.userId}
                                </TableCell>
                                <TableCell>{spin.prizeName}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={spin.prizeType}
                                    color={getPrizeTypeColor(spin.prizeType)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={spin.status}
                                    color={getStatusColor(spin.status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {new Date(spin.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                  {spin.status === 'pending' && (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Tooltip title="Claim Spin">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleClaimSpin(spin._id)}
                                          disabled={processingSpin === spin._id}
                                          color="success"
                                        >
                                          {processingSpin === spin._id ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <CheckIcon />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Cancel Spin">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleCancelSpin(spin._id)}
                                          disabled={processingSpin === spin._id}
                                          color="error"
                                        >
                                          <CancelIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {spinsTotalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                          count={spinsTotalPages}
                          page={spinsPage}
                          onChange={(e, v) => setSpinsPage(v)}
                          color="primary"
                        />
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Prize Dialog */}
      <Dialog open={prizeDialogOpen} onClose={() => setPrizeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPrize ? 'Edit Prize' : 'Add Prize'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Prize Name"
                value={prizeForm.name}
                onChange={(e) => setPrizeForm({ ...prizeForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={prizeForm.description}
                onChange={(e) => setPrizeForm({ ...prizeForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Prize Type</InputLabel>
                <Select
                  value={prizeForm.type}
                  onChange={(e) => setPrizeForm({ ...prizeForm, type: e.target.value })}
                  label="Prize Type"
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="discount">Discount</MenuItem>
                  <MenuItem value="item">Item</MenuItem>
                  <MenuItem value="points">Points</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Value"
                type="number"
                value={prizeForm.value}
                onChange={(e) => setPrizeForm({ ...prizeForm, value: e.target.value })}
                required
              />
            </Grid>
            {prizeForm.type === 'cash' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cash Amount (₦)"
                  type="number"
                  value={prizeForm.cashAmount}
                  onChange={(e) => setPrizeForm({ ...prizeForm, cashAmount: e.target.value })}
                />
              </Grid>
            )}
            {prizeForm.type === 'discount' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Discount Percentage"
                    type="number"
                    value={prizeForm.discountPercentage}
                    onChange={(e) => setPrizeForm({ ...prizeForm, discountPercentage: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Discount Code"
                    value={prizeForm.discountCode}
                    onChange={(e) => setPrizeForm({ ...prizeForm, discountCode: e.target.value })}
                  />
                </Grid>
              </>
            )}
            {prizeForm.type === 'item' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Item Name"
                    value={prizeForm.itemName}
                    onChange={(e) => setPrizeForm({ ...prizeForm, itemName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Item Description"
                    multiline
                    rows={2}
                    value={prizeForm.itemDescription}
                    onChange={(e) => setPrizeForm({ ...prizeForm, itemDescription: e.target.value })}
                  />
                </Grid>
              </>
            )}
            {prizeForm.type === 'points' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Points Amount"
                  type="number"
                  value={prizeForm.pointsAmount}
                  onChange={(e) => setPrizeForm({ ...prizeForm, pointsAmount: e.target.value })}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Image URL"
                value={prizeForm.image}
                onChange={(e) => setPrizeForm({ ...prizeForm, image: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Weight (for random selection)"
                type="number"
                value={prizeForm.weight}
                onChange={(e) => setPrizeForm({ ...prizeForm, weight: e.target.value })}
                helperText="Higher weight = more likely to be selected"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Stock (-1 for unlimited)"
                type="number"
                value={prizeForm.stock}
                onChange={(e) => setPrizeForm({ ...prizeForm, stock: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={prizeForm.displayOrder}
                onChange={(e) => setPrizeForm({ ...prizeForm, displayOrder: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={prizeForm.isActive}
                    onChange={(e) => setPrizeForm({ ...prizeForm, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrizeDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePrize} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preselected Winner Dialog */}
      <Dialog open={winnerDialogOpen} onClose={() => setWinnerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Preselected Winner</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="User ID"
                value={winnerForm.userId}
                onChange={(e) => setWinnerForm({ ...winnerForm, userId: e.target.value })}
                required
                helperText="Enter the user's ID"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Prize ID (optional - leave empty for random)"
                value={winnerForm.prizeId}
                onChange={(e) => setWinnerForm({ ...winnerForm, prizeId: e.target.value })}
                helperText="Leave empty to give random prize"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Week (YYYY-WW format, e.g., 2024-15)"
                value={winnerForm.week}
                onChange={(e) => setWinnerForm({ ...winnerForm, week: e.target.value })}
                helperText="Leave empty to use current week"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWinnerDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveWinner} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Winner Dialog */}
      <Dialog open={bulkWinnerDialogOpen} onClose={() => setBulkWinnerDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Add Preselected Winners</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Week (YYYY-WW format, e.g., 2024-15)"
                value={bulkWinnerForm.week}
                onChange={(e) => setBulkWinnerForm({ ...bulkWinnerForm, week: e.target.value })}
                helperText="Leave empty to use current week"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Winners (one per line, format: userId,prizeId or just userId)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={10}
                placeholder="userId1,prizeId1&#10;userId2&#10;userId3,prizeId3"
                onChange={(e) => {
                  const lines = e.target.value.split('\n').filter(l => l.trim());
                  const winners = lines.map(line => {
                    const parts = line.split(',').map(p => p.trim());
                    return {
                      userId: parts[0] || '',
                      prizeId: parts[1] || '',
                    };
                  });
                  setBulkWinnerForm({ ...bulkWinnerForm, winners });
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkWinnerDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveBulkWinners} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SpinManagement;

